import { BodyText, Button, Callout, Skeleton, Tooltip } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { formatDateRange } from "@/lib/time";
import { useParams } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { CirclePlus, EllipsisVertical, Eye, Send } from "lucide-react";
import { useState } from "react";
import { SectionContent } from "../components/SectionContent";
import { SectionHeader } from "../components/SectionHeader";
import { WorkNoteModal } from "./WorkNoteModal";
import { WorkNotePdfViewer } from "./WorkNotePdfViewer";
import {
  WorkNoteAction,
  useWorkNoteAvailableActions,
} from "./network/useWorkNoteAvailableActions";
import { useSendWorkNote, useWorkNotes } from "./network/useWorkNotes";
import type { WorkNoteItem } from "./types";

export const WorkNotes = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const {
    data: availableActions,
    isLoading: isActionsLoading,
    isError: isActionsError,
    refetch: refetchActions,
  } = useWorkNoteAvailableActions(patientId);
  const createAction = availableActions?.[WorkNoteAction.CREATE_WORK_NOTE];
  const canCreateWorkNote = createAction?.allowed ?? false;
  const createNotAllowedMessage = createAction?.notAllowedDebugMessage ?? null;
  const {
    data: workNotes,
    isLoading: isNotesLoading,
    isError: isNotesError,
    refetch: refetchNotes,
  } = useWorkNotes(patientId);

  const isLoading = isActionsLoading || isNotesLoading;
  const isError = isActionsError || isNotesError;

  const handleRetry = () => {
    if (isActionsError) refetchActions();
    if (isNotesError) refetchNotes();
  };

  const sendMutation = useSendWorkNote({ patientId });

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [viewingWorkNote, setViewingWorkNote] = useState<WorkNoteItem | null>(
    null,
  );

  const noWorkNotes = !isLoading && !isError && !workNotes?.length;

  const isAddDisabled = !canCreateWorkNote || isLoading || isError;

  if (viewingWorkNote) {
    return (
      <WorkNotePdfViewer
        workNote={viewingWorkNote}
        onBack={() => setViewingWorkNote(null)}
      />
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader
        title="Work Notes"
        actionButtons={[
          <Tooltip
            tooltipContentText={
              !canCreateWorkNote && createNotAllowedMessage
                ? createNotAllowedMessage
                : "Add work note"
            }
            side="bottom"
            key="add"
          >
            <Button
              variant="icon"
              className="m-0 p-0"
              onClick={() => setIsCreateModalOpen(true)}
              aria-label="Add work note"
              disabled={isAddDisabled}
            >
              <CirclePlus className="stroke-link h-4 w-4" />
            </Button>
          </Tooltip>,
        ]}
      />
      <SectionContent>
        {!canCreateWorkNote && createNotAllowedMessage && (
          <div className="flex py-2 w-full">
            <Callout
              className="w-full"
              variant="warning"
              title="Actions Unavailable"
              description={createNotAllowedMessage}
            />
          </div>
        )}
        {isLoading && (
          <div className="flex flex-col gap-3 py-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {isError && (
          <div className="flex relative flex-col gap-2 px-3 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Something went wrong!"
              description="Please click here to try again."
              onClick={handleRetry}
            />
          </div>
        )}
        {noWorkNotes && (
          <div className="flex py-2 w-full">
            <BodyText size="sm" textColor="tertiary">
              No work notes documented
            </BodyText>
          </div>
        )}
        {workNotes?.map((workNote) => (
          <div
            key={workNote.workNoteId}
            className="flex items-center justify-between px-3 py-2"
          >
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <BodyText size="sm" className="font-medium">
                Work Note - Issued{" "}
                {format(parseISO(workNote.dateIssued), "MM/dd/yyyy")}
              </BodyText>
              <BodyText size="xs" textColor="tertiary" className="truncate">
                {workNote.reason}
                {workNote.excuseStartDate && workNote.excuseEndDate && (
                  <>
                    {" \u2022 "}
                    {formatDateRange(
                      workNote.excuseStartDate,
                      workNote.excuseEndDate,
                    )}
                  </>
                )}
              </BodyText>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="icon"
                  size="sm"
                  aria-label="Work note options"
                  className="m-0 p-2 h-6 flex-shrink-0"
                >
                  <EllipsisVertical className="text-link" size={18} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={4}>
                <DropdownMenuItem onClick={() => setViewingWorkNote(workNote)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => sendMutation.mutate(workNote.workNoteId)}
                  disabled={sendMutation.isPending}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Resend
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </SectionContent>

      <WorkNoteModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
    </div>
  );
};
